/**
 * Formatting utilities for currency, percentages, and numbers
 * used throughout the CRE underwriting dashboard.
 *
 * House style (applies to every metric, dashboard, and table):
 * - Whole dollars with commas, no cents: "$1,234,567"
 * - Negative dollars in parentheses per CRE convention: "($12,500)"
 * - PSF values always show cents: "$24.50 PSF" (never "$24 PSF")
 * - Square footage: "12,500 SF" with a non-breaking space
 * - Percentages: cap/discount rates 2 decimals ("5.50%"), occupancy and
 *   growth rates 1 decimal ("94.5%")
 * - Dates: "Jan 5, 2026" (full) or "Jan 2026" (month) — never mixed numeric
 * - All formatted values use non-breaking spaces so figures never wrap
 */

/**
 * Eliminate negative zero and snap values within floating-point
 * tolerance of zero to a clean 0. Apply before any display formatting.
 */
export function cleanZero(v: number, epsilon = 0.005): number {
  if (Math.abs(v) < epsilon) return 0;
  return v;
}

const currencyFmt = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
});

const currencyDecFmt = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const numberFmt = new Intl.NumberFormat("en-US", {
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
});

const decimalFmt = new Intl.NumberFormat("en-US", {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

export function fmtCurrencyCompact(value: number | null | undefined): string {
  if (value == null) return "—";
  const abs = Math.abs(value);
  let formatted: string;
  if (abs >= 1_000_000_000) formatted = `$${(abs / 1_000_000_000).toFixed(2)}B`;
  else if (abs >= 1_000_000) formatted = `$${(abs / 1_000_000).toFixed(1)}M`;
  else if (abs >= 1_000) formatted = `$${(abs / 1_000).toFixed(0)}K`;
  else formatted = `$${abs.toFixed(0)}`;
  return value < 0 ? `(${formatted})` : formatted;
}

/** Whole-dollar currency, negatives in parentheses: "($12,500)". */
export function fmtCurrency(v: number | null | undefined): string {
  if (v == null) return "--";
  // Use epsilon=0.5 for integer-rounded currency to prevent -$0
  const cleaned = cleanZero(v, 0.5);
  if (cleaned < 0) return `(${currencyFmt.format(Math.abs(cleaned))})`;
  return currencyFmt.format(cleaned);
}

/** Accounting variant: trailing figure-space on positives balances the
 *  ")" width so mixed positive/negative columns stay digit-aligned. */
export function fmtCurrencyAcct(v: number | null | undefined): string {
  if (v == null) return "--";
  const cleaned = cleanZero(v, 0.5);
  if (cleaned < 0) return `(${currencyFmt.format(Math.abs(cleaned))})`;
  return `${currencyFmt.format(cleaned)}\u2007`;
}

/** Currency with cents, negatives in parentheses: "($1,234.56)". */
export function fmtCurrencyDec(v: number | null | undefined): string {
  if (v == null) return "--";
  const cleaned = cleanZero(v);
  if (cleaned < 0) return `(${currencyDecFmt.format(Math.abs(cleaned))})`;
  return currencyDecFmt.format(cleaned);
}

export function fmtNumber(v: number | null | undefined): string {
  if (v == null) return "--";
  return numberFmt.format(cleanZero(v));
}

export function fmtDecimal(v: number | null | undefined): string {
  if (v == null) return "--";
  return decimalFmt.format(cleanZero(v));
}

/** Percentage from a decimal fraction (0.055 → "5.5%"). Cap rates,
 *  discount rates, and IRR should pass decimals=2 ("5.50%"). */
export function fmtPercent(v: number | null | undefined, decimals = 1): string {
  if (v == null) return "--";
  return `${(cleanZero(v) * 100).toFixed(decimals)}%`;
}

export function fmtPercentRaw(v: number | null | undefined, decimals = 1): string {
  if (v == null) return "--";
  return `${cleanZero(v).toFixed(decimals)}%`;
}

export function fmtSF(v: number | null | undefined): string {
  if (v == null) return "--";
  return `${numberFmt.format(cleanZero(v))}\u00A0SF`;
}

/** Per-square-foot rate: always two decimals, "$24.50 PSF". */
export function fmtPSF(v: number | null | undefined): string {
  if (v == null) return "--";
  const cleaned = cleanZero(v);
  const abs = Math.abs(cleaned);
  const formatted = `$${decimalFmt.format(abs)}\u00A0PSF`;
  return cleaned < 0 ? `(${formatted})` : formatted;
}

export function fmtMultiple(v: number | null | undefined): string {
  if (v == null) return "--";
  return `${cleanZero(v).toFixed(2)}x`;
}

/** Parse a date string without the UTC-midnight shift: "2026-01-05" must
 *  display as Jan 5, 2026 in every timezone. Date-only strings are parsed
 *  as local dates; full ISO timestamps fall through to Date(). */
function parseDateLocal(v: string): Date {
  const m = v.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (m) return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  return new Date(v);
}

/** Full date: "Jan 5, 2026". */
export function fmtDate(v: string | null | undefined): string {
  if (!v) return "--";
  const d = parseDateLocal(v);
  if (isNaN(d.getTime())) return "--";
  return d.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

/** Month + year: "Jan 2026". Matches the fmtDate style family — never
 *  mix numeric "01/2026" with "Jan 5, 2026" in the same UI. */
export function fmtDateShort(v: string | null | undefined): string {
  if (!v) return "--";
  const d = parseDateLocal(v);
  if (isNaN(d.getTime())) return "--";
  return d.toLocaleDateString("en-US", {
    month: "short",
    year: "numeric",
  });
}

export function fmtRelativeTime(dateStr: string | null | undefined): string {
  if (!dateStr) return "--";
  const now = Date.now();
  const then = new Date(dateStr).getTime();
  const diffMs = now - then;
  if (diffMs < 0) return "just now";
  const mins = Math.floor(diffMs / 60_000);
  if (mins < 60) return `${Math.max(1, mins)}m ago`;
  const hours = Math.floor(diffMs / 3_600_000);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(diffMs / 86_400_000);
  if (days < 7) return `${days}d ago`;
  const d = new Date(dateStr);
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

export function fmtLeaseType(v: string): string {
  const map: Record<string, string> = {
    NNN: "NNN",
    NN: "NN",
    GROSS: "Gross",
    MODIFIED_GROSS: "Modified Gross",
    BASE_YEAR_STOP: "Base Year Stop",
    FSG: "Full Service Gross",
  };
  return map[v] || v;
}

export function fmtCamType(v: string): string {
  // Engine emits lowercase enum values (base_year_stop, expense_stop, ...)
  // except NNN/NN — normalize case so both forms map.
  const map: Record<string, string> = {
    nnn: "NNN",
    nn: "NN",
    base_year_stop: "Base Year Stop",
    expense_stop: "Expense Stop ($/SF)",
    expense_cap: "Expense Cap",
    gross: "Gross",
    excluded: "Excluded",
    fixed_psf: "Fixed PSF",
  };
  return map[v.toLowerCase()] || v;
}
